if (!customElements.get('tksc-product')) {
  customElements.define(
    'tksc-product',
    class TkscProduct extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('.tksc-product-form');
        this.variantInput = this.querySelector('[data-variant-id]');
        this.submitButton = this.querySelector('[data-product-submit]');
        this.submitLabel = this.querySelector('[data-product-submit-label]');
        this.price = this.querySelector('[data-product-price]');
        this.error = this.querySelector('[data-product-error]');
        this.status = this.querySelector('[data-product-status]');
        this.optionGroups = Array.from(this.querySelectorAll('[data-option-group]'));
        this.gallery = this.querySelector('[data-product-gallery]');
        this.galleryItems = Array.from(this.querySelectorAll('.tksc-product-gallery__item'));
        this.galleryCurrent = this.querySelector('[data-gallery-current]');
        this.variants = this.parseVariants();

        this.optionGroups.forEach((group) => group.addEventListener('change', () => this.handleOptionChange()));
        this.querySelectorAll('[data-tab-target]').forEach((tab) => {
          tab.addEventListener('click', () => this.selectTab(tab));
        });
        this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
        this.gallery?.addEventListener('scroll', () => this.scheduleGalleryUpdate(), { passive: true });

        this.updateVariant(false);
      }

      parseVariants() {
        const source = this.querySelector('[data-product-variants]');

        if (!source) return [];

        try {
          return JSON.parse(source.textContent);
        } catch (error) {
          console.error('Unable to parse product variants.', error);
          return [];
        }
      }

      handleOptionChange() {
        this.clearError();
        this.updateVariant(true);
      }

      updateVariant(updateUrl) {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.findVariant(selectedOptions);

        this.currentVariant = variant;
        this.updateOptionAvailability(selectedOptions);
        this.updateProductState(variant);

        if (updateUrl && variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);

          if (variant.featuredMediaId) this.showMediaById(variant.featuredMediaId);
        }
      }

      scheduleGalleryUpdate() {
        if (this.galleryAnimationFrame) return;

        this.galleryAnimationFrame = window.requestAnimationFrame(() => {
          this.galleryAnimationFrame = null;
          this.updateGalleryCounter();
        });
      }

      updateGalleryCounter() {
        if (!this.gallery || this.galleryItems.length < 2) return;

        const galleryLeft = this.gallery.getBoundingClientRect().left;
        const activeIndex = this.galleryItems.reduce(
          (closest, item, index) => {
            const distance = Math.abs(item.getBoundingClientRect().left - galleryLeft);
            return distance < closest.distance ? { index, distance } : closest;
          },
          { index: 0, distance: Number.POSITIVE_INFINITY }
        ).index;

        if (this.galleryCurrent) this.galleryCurrent.textContent = String(activeIndex + 1);
      }

      showMedia(index) {
        const item = this.galleryItems[index];
        if (!this.gallery || !item) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.gallery.scrollTo({
          left: item.offsetLeft - this.gallery.offsetLeft,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
        this.scheduleGalleryUpdate();
      }

      showMediaById(mediaId) {
        const index = this.galleryItems.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
        if (index >= 0) this.showMedia(index);
      }

      getSelectedOptions() {
        return this.optionGroups.reduce((selected, group) => {
          const input = group.querySelector('[data-option-value]:checked');
          selected[Number(group.dataset.optionIndex)] = input?.value;
          return selected;
        }, []);
      }

      findVariant(selectedOptions) {
        if (this.optionGroups.length === 0) {
          return this.variants.find((variant) => String(variant.id) === this.variantInput?.value) || this.variants[0];
        }

        return this.variants.find((variant) =>
          variant.options.every((option, index) => option === selectedOptions[index])
        );
      }

      updateOptionAvailability(selectedOptions) {
        this.optionGroups.forEach((group) => {
          const optionIndex = Number(group.dataset.optionIndex);

          group.querySelectorAll('[data-option-value]').forEach((input) => {
            const candidateOptions = [...selectedOptions];
            candidateOptions[optionIndex] = input.value;
            const isAvailable = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options.every((option, index) => !candidateOptions[index] || option === candidateOptions[index])
            );

            input.classList.toggle('is-unavailable', !isAvailable);
          });
        });
      }

      updateProductState(variant) {
        if (this.variantInput) {
          this.variantInput.disabled = !variant;
          this.variantInput.value = variant?.id || '';
        }

        if (this.price) this.price.textContent = variant?.formattedPrice || '';

        const isAvailable = Boolean(variant?.available);
        if (this.submitButton) this.submitButton.disabled = !isAvailable;

        if (!this.submitLabel || !this.submitButton) return;

        this.submitLabel.textContent = variant
          ? isAvailable
            ? this.submitButton.dataset.addLabel
            : this.submitButton.dataset.soldOutLabel
          : this.submitButton.dataset.unavailableLabel;
      }

      selectTab(tab) {
        if (tab.disabled) return;

        const target = tab.dataset.tabTarget;
        this.querySelectorAll('[data-tab-target]').forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-selected', String(isActive));
        });
        this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.tabPanel !== target;
        });
      }

      async handleSubmit(event) {
        event.preventDefault();

        if (!this.currentVariant?.available || !this.form || !this.submitButton) return;

        this.clearError();
        this.setLoading(true);

        try {
          const formData = new FormData(this.form);
          formData.set('id', this.currentVariant.id);
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });
          const result = await response.json();

          if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');

          await this.updateCartCount();

          this.status.textContent = `${result.product_title || 'Product'} added to cart.`;
          this.submitLabel.textContent = 'ADDED';
          window.setTimeout(() => this.updateProductState(this.currentVariant), 1400);
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }

      async updateCartCount() {
        const counters = document.querySelectorAll('[data-cart-count]');

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) throw new Error('Unable to refresh cart count.');

          const cart = await response.json();
          counters.forEach((count) => {
            count.textContent = cart.item_count;
          });
        } catch (error) {
          counters.forEach((count) => {
            count.textContent = Number(count.textContent || 0) + 1;
          });
          console.warn(error);
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.setAttribute('aria-busy', String(isLoading));
        this.submitButton.disabled = isLoading || !this.currentVariant?.available;
      }

      clearError() {
        if (!this.error) return;

        this.error.hidden = true;
        this.error.textContent = '';
      }

      showError(message) {
        if (!this.error) return;

        this.error.textContent = message;
        this.error.hidden = false;
      }
    }
  );
}
